import apiClient from "../../client/apiClient"

// ── Types ──────────────────────────────────────────────────────────
export type CertSignature = {
  _id: string
  signerName: string
  signerTitle: string
  imageKey: string
  imageUrl?: string // presigned URL for thumbnail preview
  branch?: { _id: string; name: string; campusNo?: string } | null
  isActive: boolean
  createdAt?: string
}
export type CertAttribution = {
  _id: string
  name: string
  title?: string | null
  description?: string | null
  imageKey?: string | null
  isActive: boolean
}
export type CertFont = {
  _id: string
  name: string
  family: string
  fileKey: string
  fileUrl?: string // presigned URL — used to inject @font-face for live preview
  format: string
  isActive: boolean
}
export type CertCompetition = {
  _id: string
  name: string
  description?: string | null
  category?: string | null
  isActive: boolean
}
export type CertSport = { _id: string; name: string; isActive: boolean }
export type CertCanvasPreset = { _id: string; name: string; payload: any }
export type CertTemplate = {
  _id: string
  name: string
  certificateType: string
  definition: any
  masterTemplate?: { _id: string; name: string } | null
  branch?: any
  classes?: { _id: string; name: string }[]
  isActive: boolean
  updatedAt?: string
}
export type CertMasterTemplate = {
  _id: string
  name: string
  certificateType: string
  definition: any
  isActive: boolean
  linkedTemplates?: number
  updatedAt?: string
}
export type CertBatch = {
  _id: string
  type: string
  fileName: string
  session?: any
  branch?: any
  studentsData: any[]
  attribution?: any
  message?: string | null
  class?: any
  refNumber?: string | null
  lastGeneratedAt?: string | null
  createdAt?: string
}
export type CertRecord = {
  _id: string
  template?: any
  certificateType: string
  session?: any
  branch?: any
  class?: any
  studentCount: number
  pageFormat?: string | null
  pdfKey: string
  generatedAt: string
  generatedBy?: any
}

type ListResp<T> = { success: boolean; data: T[] }
type OneResp<T> = { success: boolean; data: T }

const unwrap = (r: any) => r.data

// ── Generic CRUD factory ───────────────────────────────────────────
const crud = <T,>(base: string) => ({
  list: async (params?: Record<string, any>): Promise<ListResp<T>> =>
    unwrap(await apiClient({ method: "GET", url: base, params })),
  get: async (id: string): Promise<OneResp<T>> =>
    unwrap(await apiClient({ method: "GET", url: `${base}/${id}` })),
  create: async (data: any): Promise<OneResp<T>> =>
    unwrap(await apiClient({ method: "POST", url: base, data })),
  update: async (id: string, data: any): Promise<OneResp<T>> =>
    unwrap(await apiClient({ method: "PUT", url: `${base}/${id}`, data })),
  remove: async (id: string): Promise<{ success: boolean }> =>
    unwrap(await apiClient({ method: "DELETE", url: `${base}/${id}` })),
  restore: async (id: string): Promise<OneResp<T>> =>
    unwrap(await apiClient({ method: "PUT", url: `${base}/${id}/restore` })),
})

// ── Branches (for Signatures branch filter) ────────────────────────
export const certBranchesApi = async (): Promise<{ success: boolean; data: { _id: string; name: string; campusNo?: string; code?: string; schoolName?: string; schoolCode?: string; logoKey?: string; logoUrl?: string | null; blackLogoKey?: string; blackLogoUrl?: string | null; strokeLogoKey?: string; strokeLogoUrl?: string | null; address?: string; contactNumber?: string; systems?: { _id: string; name: string; code?: string; style?: "local" | "cambridge" }[] }[] }> =>
  unwrap(await apiClient({ method: "GET", url: "/certificates/branches" }))

// ── Master data ────────────────────────────────────────────────────
export const signaturesApi = crud<CertSignature>("/certificates/signatures")
export const attributionsApi = crud<CertAttribution>("/certificates/attributions")
export const fontsApi = crud<CertFont>("/certificates/fonts")
export const competitionsApi = crud<CertCompetition>("/certificates/competitions")
export const sportsApi = crud<CertSport>("/certificates/sports")
export const canvasPresetsApi = crud<CertCanvasPreset>("/certificates/canvas-presets")

// ── Templates ──────────────────────────────────────────────────────
export const templatesApi = {
  ...crud<CertTemplate>("/certificates/templates"),
  setClasses: async (id: string, classes: string[]) =>
    unwrap(await apiClient({ method: "PUT", url: `/certificates/templates/${id}/classes`, data: { classes } })),
  duplicate: async (id: string, name?: string) =>
    unwrap(await apiClient({ method: "POST", url: `/certificates/templates/${id}/duplicate`, data: { name } })),
  restore: async (id: string) =>
    unwrap(await apiClient({ method: "PUT", url: `/certificates/templates/${id}/restore` })),
}
export const masterTemplatesApi = {
  ...crud<CertMasterTemplate>("/certificates/master-templates"),
  duplicate: async (id: string, name?: string): Promise<OneResp<CertMasterTemplate>> =>
    unwrap(await apiClient({ method: "POST", url: `/certificates/master-templates/${id}/duplicate`, data: { name } })),
  restore: async (id: string): Promise<OneResp<CertMasterTemplate>> =>
    unwrap(await apiClient({ method: "PUT", url: `/certificates/master-templates/${id}/restore` })),
}

// Render a sample PDF from a (possibly unsaved) definition → presigned URL.
export const samplePdfApi = async (definition: any, certificateType: string): Promise<{ success: boolean; url: string }> =>
  unwrap(await apiClient({ method: "POST", url: "/certificates/sample-pdf", data: { definition, certificateType } }))

// ── Batches / generation / records ─────────────────────────────────
export const batchesApi = {
  ...crud<CertBatch>("/certificates/batches"),
  // Starts a background job; returns { jobId }. Poll jobsApi.get(jobId).
  generate: async (id: string, data: any): Promise<{ success: boolean; jobId: string }> =>
    unwrap(await apiClient({ method: "POST", url: `/certificates/batches/${id}/generate`, data })),
  merge: async (data: { batchIds: string[]; fileName?: string }) =>
    unwrap(await apiClient({ method: "POST", url: "/certificates/batches/merge", data })),
}

// Class → template map for auto-routing. Returns { [classId]: { id, name } }.
export const classMapApi = async (type: string): Promise<{ success: boolean; data: Record<string, { id: string; name: string }> }> =>
  unwrap(await apiClient({ method: "GET", url: "/certificates/templates/class-map", params: { type } }))

// Poll a generation job. status: queued | running | completed | failed.
export const jobsApi = {
  get: async (id: string) => unwrap(await apiClient({ method: "GET", url: `/certificates/jobs/${id}` })),
}
export const generateApi = async (type: string, data: any) =>
  unwrap(await apiClient({ method: "POST", url: `/certificates/generate/${type}`, data }))

export const studentsForGenerationApi = async (params: Record<string, any>) =>
  unwrap(await apiClient({ method: "GET", url: "/certificates/students", params }))

// Resolve specific students by GR number (comma-separated) for the
// "Add by GR" entry on the Generate page.
export const studentsByGrApi = async (params: { session?: string; grNumbers: string }) =>
  unwrap(await apiClient({ method: "GET", url: "/certificates/students/by-gr", params }))

// Data-driven roster suggestions (merit certs).
export const suggestApi = {
  topPerformers: async (params: { session?: string; class?: string; assessment?: string; limit?: number }) =>
    unwrap(await apiClient({ method: "GET", url: "/certificates/suggest/top-performers", params })),
  attendanceEligible: async (params: { session?: string; class?: string; section?: string; threshold?: number }) =>
    unwrap(await apiClient({ method: "GET", url: "/certificates/suggest/attendance-eligible", params })),
  teachers: async (params: { branch?: string; search?: string }) =>
    unwrap(await apiClient({ method: "GET", url: "/certificates/suggest/teachers", params })),
}

// Designer asset upload → { key, url }. `url` is a viewable presigned link.
export const uploadDesignerAssetApi = async (file: File) => {
  const fd = new FormData()
  fd.append("image", file)
  return unwrap(await apiClient({ method: "POST", url: "/certificates/assets/upload", data: fd }))
}
export const assetUrlApi = async (key: string): Promise<{ success: boolean; url: string }> =>
  unwrap(await apiClient({ method: "GET", url: "/certificates/asset-url", params: { key } }))

// Site settings (school name/code/address/phone/logo).
export const siteSettingsApi = {
  get: async () => unwrap(await apiClient({ method: "GET", url: "/certificates/site-settings" })),
  update: async (data: FormData) =>
    unwrap(await apiClient({ method: "PUT", url: "/certificates/site-settings", data })),
}

export const recordsApi = {
  list: async (params?: Record<string, any>) =>
    unwrap(await apiClient({ method: "GET", url: "/certificates/records", params })),
  get: async (id: string) =>
    unwrap(await apiClient({ method: "GET", url: `/certificates/records/${id}` })),
  pdfUrl: async (id: string): Promise<{ success: boolean; url: string }> =>
    unwrap(await apiClient({ method: "GET", url: `/certificates/records/${id}/pdf` })),
  remove: async (id: string) =>
    unwrap(await apiClient({ method: "DELETE", url: `/certificates/records/${id}` })),
  // Start a ZIP-download job for filtered records. Poll jobsApi.get(jobId).
  zip: async (params: Record<string, any>): Promise<{ success: boolean; jobId: string }> =>
    unwrap(await apiClient({ method: "POST", url: "/certificates/records/zip", data: params })),
}

// Helper: build a multipart FormData payload for uploads (signatures,
// attributions, fonts). `fileField` is the form field the route expects.
export const buildUploadForm = (
  fields: Record<string, string | undefined>,
  file?: File | null,
  fileField = "image"
) => {
  const fd = new FormData()
  Object.entries(fields).forEach(([k, v]) => {
    if (v !== undefined && v !== null) fd.append(k, v)
  })
  if (file) fd.append(fileField, file)
  return fd
}

// Certificate type taxonomy — mirrors the RMS two-category structure
// (Certificate of Appreciation / Certificate of Merit) with sub-types.
// Sports + Competition are folded into the unified "Winner Certificate"
// (use its event_type field to pick sport vs competition), matching RMS.
export interface CertTypeLeaf {
  value: string
  label: string
}
export const CERT_CATEGORIES: { label: string; types: CertTypeLeaf[] }[] = [
  {
    label: "Certificate of Appreciation",
    types: [
      { value: "participation", label: "Participation" },
      { value: "appreciation", label: "Attribution" },
      { value: "general", label: "General" },
    ],
  },
  {
    label: "Certificate of Merit",
    types: [
      { value: "class_result", label: "Class Result" },
      { value: "winner", label: "Winner Certificate" },
      { value: "best_teacher", label: "Best Teacher" },
      { value: "attendance", label: "100% Attendance" },
      { value: "graduation", label: "Graduation" },
      { value: "outstanding", label: "Outstanding Performance" },
      { value: "cooperative_parents", label: "Cooperative Parents" },
    ],
  },
]

// Flat list — for label lookups + filters. Includes legacy sports/
// competition labels so older records still show a friendly name.
export const CERT_TYPES: CertTypeLeaf[] = [
  ...CERT_CATEGORIES.flatMap(c => c.types),
  { value: "sports", label: "Sports" },
  { value: "competition", label: "Competition" },
]
